<template>
  <div class="css-v2 gc-note-attachments">
    <div class="gc-note-attachments-label">Attachments</div>
    <div v-if="errorMsg" class="gc-note-attachments-error">{{ errorMsg }}</div>
    <div class="attach-area">
      <div
        class="attach-tile"
        v-for="(file, index) in attachments"
        :key="file.RecordId + '-' + index"
        @click="openPreview(file)"
      >
        <div class="thumb">
          <img v-if="isImage(file)" :src="fileSrc(file)" />
          <span v-else class="dx-icon-doc thumb-icon"></span>
        </div>
        <div class="fname" :title="displayFileName(file.FileName)">
          {{ displayFileName(file.FileName) }}
        </div>
        <div
          class="rmicon"
          v-if="!readOnly"
          @click.stop="removeFile(index)"
          title="Remove"
        >
          <span class="dx-icon-close"></span>
        </div>
      </div>
      <div
        class="dropzone"
        v-if="!readOnly"
        :class="{ dragover: isDragging }"
        @click="triggerFileInput"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="onDrop"
      >
        <input
          ref="fileInput"
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
          style="display: none"
          @change="onFilesSelected"
        />
        <span class="dx-icon-upload"></span>
        <span>Drop files or click to upload</span>
        <span class="dz-hint">10 MB each</span>
      </div>
    </div>

    <DxPopup
      :visible="previewVisible"
      :title="previewFile ? displayFileName(previewFile.FileName) : ''"
      :drag-enabled="false"
      :show-title="true"
      :show-close-button="true"
      :hide-on-outside-click="true"
      width="60%"
      height="75%"
      :wrapperAttr="{ class: 'css-v2 gc-note-attachment-preview-popup' }"
      @hidden="closePreview"
    >
      <template #content>
        <div class="gc-note-attachment-preview-content">
          <div class="preview-body">
            <img v-if="previewFile && isImage(previewFile)" :src="fileSrc(previewFile)" />
            <iframe
              v-else-if="previewFile && isPdf(previewFile)"
              :src="fileSrc(previewFile)"
            ></iframe>
            <div v-else-if="previewFile" class="preview-placeholder">
              <span class="dx-icon-doc"></span>
              <div>{{ displayFileName(previewFile.FileName) }}</div>
            </div>
          </div>
          <div class="preview-footer">
            <DxButton text="Back" styling-mode="outlined" @click="closePreview" />
            <div class="preview-footer-right">
              <DxButton
                v-if="!readOnly"
                text="Remove"
                type="danger"
                styling-mode="contained"
                @click="removeCurrentPreview"
              />
              <DxButton
                text="Download"
                type="default"
                styling-mode="contained"
                @click="downloadCurrentPreview"
              />
            </div>
          </div>
        </div>
      </template>
    </DxPopup>
  </div>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { DxButton } from "devextreme-vue/button";
import appConfig from "../../app-config";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
];

export default {
  name: "GcSalesContractNoteAttachments",
  components: {
    DxPopup,
    DxButton,
  },
  props: {
    attachments: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false,
      errorMsg: "",
      errorTimer: null,
      previewVisible: false,
      previewFile: null,
    };
  },
  beforeDestroy() {
    clearTimeout(this.errorTimer);
  },
  methods: {
    displayFileName(fileName) {
      // Newly-uploaded files are stored server-side as "{36-char guid}_{originalName}"
      if (fileName && fileName.length > 37 && fileName.charAt(36) === "_") {
        return fileName.substring(37);
      }
      return fileName;
    },
    isImage(file) {
      return (file.ContentType || "").indexOf("image/") === 0;
    },
    isPdf(file) {
      return file.ContentType === "application/pdf";
    },
    fileSrc(file) {
      return file.MediaBase64String || file.MediaUrl;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFilesSelected(e) {
      this.processFiles(Array.from(e.target.files));
      e.target.value = "";
    },
    onDrop(e) {
      this.isDragging = false;
      this.processFiles(Array.from(e.dataTransfer.files));
    },
    processFiles(files) {
      files.forEach((file) => {
        if (ACCEPTED_TYPES.indexOf(file.type) === -1) {
          this.showError(
            `"${file.name}" is not a supported file type. Please upload JPEG, PNG, PDF, Word, Excel, CSV, or TXT.`
          );
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          this.showError(`"${file.name}" exceeds the 10 MB limit.`);
          return;
        }
        this.readFileAsBase64(file);
      });
    },
    readFileAsBase64(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const updated = this.attachments.concat([
          {
            RecordId: "00000000-0000-0000-0000-000000000000",
            FileName: file.name,
            ContentType: file.type,
            FileSize: file.size,
            MediaBase64String: e.target.result,
          },
        ]);
        this.$emit("change", updated);
      };
      reader.readAsDataURL(file);
    },
    removeFile(index) {
      const updated = this.attachments.slice();
      updated.splice(index, 1);
      this.$emit("change", updated);
    },
    showError(msg) {
      this.errorMsg = msg;
      clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => {
        this.errorMsg = "";
      }, 5000);
    },
    openPreview(file) {
      this.previewFile = file;
      this.previewVisible = true;
    },
    closePreview() {
      this.previewVisible = false;
      this.previewFile = null;
    },
    removeCurrentPreview() {
      const index = this.attachments.indexOf(this.previewFile);
      this.closePreview();
      if (index > -1) this.removeFile(index);
    },
    downloadCurrentPreview() {
      if (!this.previewFile) return;
      const file = this.previewFile;

      if (file.BlobName) {
        const url = `${appConfig.VUE_APP_ROOT_API}/api/TransactionMedia/${file.BlobName}/Download`;
        fetch(url)
          .then((res) => res.blob())
          .then((blob) => {
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = this.displayFileName(file.FileName) || "download";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          });
      } else if (file.MediaBase64String) {
        const link = document.createElement("a");
        link.href = file.MediaBase64String;
        link.download = file.FileName || "download";
        link.click();
      }
    },
  },
};
</script>

<style lang="scss">
.gc-note-attachments {
  margin-top: 12px;

  .gc-note-attachments-label {
    font-size: 12px;
    font-weight: 500;
    color: #344054;
    margin-bottom: 8px;
  }

  .gc-note-attachments-error {
    font-size: 12px;
    color: #dc2626;
    margin-bottom: 8px;
  }

  .attach-area {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .attach-tile {
    width: 110px;
    border: 1px solid #eaecf0;
    border-radius: 6px;
    padding: 8px;
    position: relative;
    cursor: pointer;
    background: #fafbfc;

    .thumb {
      width: 100%;
      height: 64px;
      border-radius: 4px;
      background: #eef1f4;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      margin-bottom: 6px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .thumb-icon {
        font-size: 26px;
        color: #98a2b3;
      }
    }

    .fname {
      font-size: 10px;
      color: #667085;
      word-break: break-word;
      line-height: 1.3;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .rmicon {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 18px;
      height: 18px;
      background: #fff;
      border: 1px solid #eaecf0;
      border-radius: 50%;
      display: none;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      .dx-icon-close {
        font-size: 10px;
        color: #dc2626;
      }
    }

    &:hover .rmicon {
      display: flex;
    }
  }

  .dropzone {
    width: 110px;
    height: 100px;
    border: 2px dashed #d0d5dd;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: pointer;
    color: #667085;
    font-size: 10px;
    text-align: center;
    padding: 6px;

    &:hover,
    &.dragover {
      border-color: #35baf6;
      color: #35baf6;
      background: #f0f9ff;
    }

    .dz-hint {
      color: #98a2b3;
      font-size: 9px;
    }
  }
}

.gc-note-attachment-preview-popup {
  .dx-popup-content {
    height: 100%;
  }

  .gc-note-attachment-preview-content {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .preview-body {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    iframe {
      width: 100%;
      height: 100%;
      border: none;
    }

    .preview-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      color: #667085;

      .dx-icon-doc {
        font-size: 48px;
      }
    }
  }

  .preview-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 12px;
    border-top: 1px solid #eaecf0;

    .preview-footer-right {
      display: flex;
      gap: 10px;
    }
  }
}
</style>
